import { createSelector } from '@ngrx/store';

import { Helpers } from '@core/common-legacy';
import { createSingleEntityStore, ngrxEvent, pipeState } from '@core/ngrx-legacy';
import {
  TellentGettingStartedProgressResponse,
  TellentGettingStartedStepViewModel,
  TellentGettingStartedResponse,
} from '@core/tellent-getting-started-types';

export const {
  actions: TellentGettingStartedProgressActions,
  selectors: TellentGettingStartedProgressSelectors,
  reducer: TellentGettingStartedProgressReducer,
  namespace: TellentGettingStartedProgressNamespace,
} = createSingleEntityStore<TellentGettingStartedProgressResponse>()(
  'TellentGettingStartedProgress',
  {
    customActions: adapter => ({
      updateStepProgress: (state, payload: string[]) =>
        pipeState(state, next => adapter.markAsPending(next)),
      updateStepProgressSuccess: (state, payload: TellentGettingStartedProgressResponse) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      updateStepProgressFailed: ngrxEvent<void>(),

      dismissGuide: ngrxEvent<void>(),
      dismissGuideSuccess: (state, payload: TellentGettingStartedProgressResponse) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      dismissGuideFailed: ngrxEvent<void>(),
    }),
  },
);

export const {
  actions: TellentGettingStartedContentActions,
  selectors: TellentGettingStartedContentSelectors,
  reducer: TellentGettingStartedContentReducer,
  namespace: TellentGettingStartedContentNamespace,
} = createSingleEntityStore<TellentGettingStartedResponse>()('TellentGettingStartedContent');

const isDrawerReady = createSelector(
  TellentGettingStartedProgressSelectors.meta,
  TellentGettingStartedContentSelectors.meta,
  (progressMeta, contentMeta) => progressMeta.isLoaded && contentMeta.isLoaded,
);

const isDrawerTriggerVisible = createSelector(TellentGettingStartedProgressSelectors.data, data =>
  data ? !data.hideTopbar && data.totalSteps > 0 : false,
);

const drawerViewModel = (locale: string) =>
  createSelector(TellentGettingStartedContentSelectors.data, content =>
    content ? (content.drawer[locale] ?? content.drawer['en']) : null,
  );

const stepsViewModel = (locale: string) =>
  createSelector(
    TellentGettingStartedContentSelectors.data,
    TellentGettingStartedProgressSelectors.data,
    (content, progress): TellentGettingStartedStepViewModel[] => {
      if (!content || !progress) return [];

      return content.steps.map(({ translations: stepTranslations, ...step }) => {
        const stepTrans = stepTranslations[locale] ?? stepTranslations['en'] ?? {};
        const subtasks = step.subtasks.map(({ translations: subtaskTranslations, ...subtask }) => {
          const subtaskTrans = subtaskTranslations[locale] ?? subtaskTranslations['en'] ?? {};

          return {
            ...subtask,
            ...subtaskTrans,
            completed: progress.stepStatuses[Helpers.camelize(subtask.key)].status === 'completed',
          };
        });

        return {
          ...step,
          ...stepTrans,
          subtasks,
          progress: subtasks.filter(({ completed }) => completed).length,
        };
      });
    },
  );

export const TellentGettingStartedSelectors = {
  isDrawerReady,
  isDrawerTriggerVisible,
  stepsViewModel,
  drawerViewModel,
};
